import { Route, Navigate } from 'react-router-dom';
import Home from '@pages/user/home/Home';
import AdminHome from '@pages/admin/AdminHome';
import Login from '@pages/user/Login';
import AdminWorkspace from '@pages/admin/AdminWorkspace';
import Register from '@pages/user/Register';
import AdminProduct from '@pages/admin/AdminProduct';
import Order from '@pages/user/order/Order';
import AdminAccount from '@pages/admin/AdminAccount';
import LoadingModal from '@components/common/modal/LoadingModal';
import AuthErrorListener from '@components/common/AuthErrorListener';
import { Global } from '@emotion/react';
import { globalStyles } from '@styles/globalStyles';
import OrderBasket from '@pages/user/order/OrderBasket';
import OrderPay from '@pages/user/order/OrderPay';
import OrderComplete from '@pages/user/order/OrderComplete';
import AdminOrderStatistics from '@pages/admin/order/AdminOrderStatistics';
import AdminMyInfo from '@pages/admin/AdminMyInfo';
import AdminProductCategories from '@pages/admin/AdminProductCategories';
import ResetPassword from '@pages/user/ResetPassword';
import SuperAdminWorkspace from '@pages/super-admin/SuperAdminWorkspace';
import SuperAdminUser from '@pages/super-admin/SuperAdminUser';
import AdminOrderRealtime from '@pages/admin/order/AdminOrderRealtime';
import SuperAdminEmailDomainList from '@pages/super-admin/SuperAdminEmailDomainList';
import UserEmailDomain from '@pages/user/UserEmailDomain';
import AdminWorkspaceEdit from '@pages/admin/AdminWorkspaceEdit';
import SuperAdminBank from '@pages/super-admin/SuperAdminBank';
import SuperAdminDashboard from '@pages/super-admin/SuperAdminDashboard';
import SuperAdminAcquisitionSurvey from '@pages/super-admin/SuperAdminAcquisitionSurvey';
import SuperAdminDonationClicks from '@pages/super-admin/SuperAdminDonationClicks';
import SuperAdminOrders from '@pages/super-admin/SuperAdminOrders';
import SuperAdminFestivalCalendar from '@pages/super-admin/SuperAdminFestivalCalendar';
import SuperAdminCache from '@pages/super-admin/SuperAdminCache';
import SuperAdminInquiryList from '@pages/super-admin/SuperAdminInquiryList';
import SuperAdminInquiryDetail from '@pages/super-admin/SuperAdminInquiryDetail';
import { ToastContainer } from 'react-toastify';
import useNetworkStatusNotifier from '@hooks/useNetworkStatusNotifier';
import useServerHealth from '@hooks/useServerHealth';
import useScrollToTop from '@hooks/useScrollToTop';
import useSentryContext from '@hooks/common/useSentryContext';
import useAnalytics from '@hooks/common/useAnalytics';
import useAppShellReveal from '@hooks/common/useAppShellReveal';
import ServerErrorFallback from '@components/common/fallback/ServerErrorFallback';
import OrderWait from '@pages/user/order/OrderWait';
import AdminTotalOrder from '@pages/admin/order/AdminTotalOrder';
import SentryTestPage from '@components/common/test/SentryTestPage';
import { SentryRoutes } from 'src';
import { ADMIN_ROUTES, ORDER_ROUTES, SUPER_ADMIN_ROUTES, TEST_ROUTES, USER_ROUTES } from '@constants/routes';
import AdminOrderTimeline from '@pages/admin/order/AdminOrderTimeline';
import AdminTableRealtime from '@pages/admin/table/AdminTableRealtime';
import Info from '@pages/user/info/Info';
import Contact from '@pages/user/contact/Contact';
import Privacy from '@pages/user/privacy/Privacy';
import PcOnlyLayout from '@components/common/layout/PcOnlyLayout';

function App() {
  useAppShellReveal();
  useSentryContext();
  useAnalytics();
  useNetworkStatusNotifier();
  useScrollToTop();
  const { isServerHealthy, isChecking, error, manualRetry } = useServerHealth();

  if (!isServerHealthy && !isChecking) {
    return <ServerErrorFallback error={error} onRetry={manualRetry} isRetrying={isChecking} />;
  }

  return (
    <>
      <AuthErrorListener />
      <ToastContainer
        position="top-right"
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <SentryRoutes>
        <Route path={USER_ROUTES.HOME} element={<Home />} />
        <Route path={USER_ROUTES.INFO} element={<Info />} />
        <Route path={USER_ROUTES.CONTACT} element={<Contact />} />
        <Route path={USER_ROUTES.PRIVACY} element={<Privacy />} />

        <Route path={ORDER_ROUTES.ORDER} element={<Order />} />
        <Route path={ORDER_ROUTES.ORDER_BASKET} element={<OrderBasket />} />
        <Route path={ORDER_ROUTES.ORDER_PAY} element={<OrderPay />} />
        <Route path={ORDER_ROUTES.ORDER_WAIT} element={<OrderWait />} />
        <Route path={ORDER_ROUTES.ORDER_COMPLETE} element={<OrderComplete />} />

        <Route path={SUPER_ADMIN_ROUTES.HOME} element={<Navigate to={SUPER_ADMIN_ROUTES.DASHBOARD} replace />} />
        <Route path={SUPER_ADMIN_ROUTES.WORKSPACE} element={<SuperAdminWorkspace />} />
        <Route path={SUPER_ADMIN_ROUTES.USER} element={<SuperAdminUser />} />
        <Route path={SUPER_ADMIN_ROUTES.EMAIL} element={<SuperAdminEmailDomainList />} />
        <Route path={SUPER_ADMIN_ROUTES.BANK} element={<SuperAdminBank />} />
        <Route path={SUPER_ADMIN_ROUTES.DASHBOARD} element={<SuperAdminDashboard />} />
        <Route path={SUPER_ADMIN_ROUTES.ORDERS} element={<SuperAdminOrders />} />
        <Route path={SUPER_ADMIN_ROUTES.FESTIVAL_CALENDAR} element={<SuperAdminFestivalCalendar />} />
        <Route path={SUPER_ADMIN_ROUTES.CACHE} element={<SuperAdminCache />} />
        <Route path={SUPER_ADMIN_ROUTES.INQUIRIES} element={<SuperAdminInquiryList />} />
        <Route path={SUPER_ADMIN_ROUTES.INQUIRY_DETAIL} element={<SuperAdminInquiryDetail />} />
        <Route path={SUPER_ADMIN_ROUTES.ACQUISITION_SURVEY} element={<SuperAdminAcquisitionSurvey />} />
        <Route path={SUPER_ADMIN_ROUTES.DONATION_CLICKS} element={<SuperAdminDonationClicks />} />

        <Route path={USER_ROUTES.LOGIN} element={<Login />} />
        <Route path={ADMIN_ROUTES.MY_INFO} element={<AdminMyInfo />} />

        <Route element={<PcOnlyLayout />}>
          <Route path={USER_ROUTES.REGISTER} element={<Register />} />
          <Route path={USER_ROUTES.RESET_PASSWORD} element={<ResetPassword />} />
          <Route path={USER_ROUTES.EMAIL_DOMAINS} element={<UserEmailDomain />} />

          <Route path={ADMIN_ROUTES.HOME} element={<AdminHome />} />
          <Route path={ADMIN_ROUTES.REGISTER_ACCOUNT} element={<AdminAccount />} />
          <Route path={ADMIN_ROUTES.WORKSPACE} element={<AdminWorkspace />} />
          <Route path={ADMIN_ROUTES.WORKSPACE_EDIT} element={<AdminWorkspaceEdit />} />
          <Route path={ADMIN_ROUTES.ORDER_REALTIME} element={<AdminOrderRealtime />} />
          <Route path={ADMIN_ROUTES.ORDER_STATISTICS} element={<AdminOrderStatistics />} />
          <Route path={ADMIN_ROUTES.TABLE_REALTIME} element={<AdminTableRealtime />} />
          <Route path={ADMIN_ROUTES.ORDER_TIMELINE} element={<AdminOrderTimeline />} />
          <Route path={ADMIN_ROUTES.TOTAL_ORDER} element={<AdminTotalOrder />} />
          <Route path={ADMIN_ROUTES.PRODUCTS} element={<AdminProduct />} />
          <Route path={ADMIN_ROUTES.PRODUCTS_CATEGORIES} element={<AdminProductCategories />} />

          <Route path={TEST_ROUTES.SENTRY_TEST} element={<SentryTestPage />} />
        </Route>
      </SentryRoutes>
      <Global styles={globalStyles} />
      <LoadingModal />
    </>
  );
}

export default App;
